import { useLayoutEffect, useRef, useState, useEffect } from 'react';

/** Return computed style property */
export const useComputedStyleProperty = (propertyName: string, initValue?: string) => {
	const [value, setValue] = useState(initValue);

	const elRef = useRef(null);

	useLayoutEffect(() => {
		if (elRef.current) {
			const value = getComputedStyle(elRef.current).getPropertyValue(propertyName).trim();
			if (value.length > 0) {
				setValue(value);
			}
		}
	}, []);

	return { value, elRef };
};

export const useKeyboardHeight = () => {
	const [keyboardHeight, setKeyboardHeight] = useState(0);
	const baselineHeightRef = useRef<number>(0);
	const hasInputFocusRef = useRef(false);

	useEffect(() => {
		const visualViewport = window.visualViewport;

		if (!visualViewport) {
			return undefined;
		}

		// baselineHeightRef - высота экрана без клавиатуры
		// для iOS это критично, потому что innerHeight меняется при открытии клавиатуры
		baselineHeightRef.current = Math.max(window.innerHeight, document.documentElement.clientHeight);

		const handleFocusIn = (e: FocusEvent) => {
			const target = e.target as HTMLElement;
			if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
				hasInputFocusRef.current = true;
			}
		};

		const handleFocusOut = () => {
			hasInputFocusRef.current = false;
		};

		let raf = 0;

		const computeInset = () => {
			// Если нет фокуса на input, клавиатуры точно нет (исправляет баг ресайза по высоте в девтулс)
			if (!hasInputFocusRef.current) {
				return 0;
			}

			const baseline = baselineHeightRef.current;

			// iOS: baseline не меняется при открытии клавиатуры,
			// visualViewport.height уменьшается,
			// visualViewport.offsetTop может меняться при скролле
			const insetFromBaseline = Math.max(0, baseline - (visualViewport.height + visualViewport.offsetTop));

			// Android: на Android baseline часто === innerHeight (не меняется),
			// но visualViewport.offsetTop растёт при скролле с клавиатурой
			const insetFromInner = Math.max(0, window.innerHeight - (visualViewport.height + visualViewport.offsetTop));

			return Math.max(insetFromBaseline, insetFromInner);
		};

		const update = () => {
			cancelAnimationFrame(raf);

			// запускает код перед следующим кадром перерисовки браузера
			raf = requestAnimationFrame(() => {
				const inset = computeInset();

				// если клавиатуры нет — обновляем baseline
				// (например, при смене ориентации / скрытии адресной строки)
				if (inset === 0 && !hasInputFocusRef.current) {
					baselineHeightRef.current = Math.max(window.innerHeight, document.documentElement.clientHeight);
				}

				setKeyboardHeight(inset);
			});
		};

		update();

		visualViewport.addEventListener('resize', update);
		visualViewport.addEventListener('scroll', update);

		window.addEventListener('resize', update);
		document.addEventListener('focusin', handleFocusIn);
		document.addEventListener('focusout', handleFocusOut);

		return () => {
			cancelAnimationFrame(raf);

			visualViewport.removeEventListener('resize', update);
			visualViewport.removeEventListener('scroll', update);

			window.removeEventListener('resize', update);
			document.removeEventListener('focusin', handleFocusIn);
			document.removeEventListener('focusout', handleFocusOut);
		};
	}, []);

	return keyboardHeight;
};
